import { ILinkItem } from '../types/LinkItem.interface';
import { isValidUrl } from '@internal/ui-common';
import { MoveDirection } from '../types/MoveDirection.enum';
import { PaletteColor } from '@mui/material/styles';
import { useStyles } from './favoriteLinksSettingsDialog.style';

import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { LocalStorageHelper, LocalStorageKeys } from '@internal/ui-common';
import { Table, TableColumn } from '@backstage/core-components';
import { useEffect, useMemo, useRef, useState } from 'react';

interface FavoriteLinksArrayWidgetProps {
  value: ILinkItem[];
  onChange: (newLinks: ILinkItem[]) => void;
}

export function FavoriteLinksSettingsDialog({
  value = [],
  onChange,
}: FavoriteLinksArrayWidgetProps) {
  const { cx, classes } = useStyles();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState({ name: '', href: '', color: '' });
  const [widgetDimensions, setWidgetDimensions] = useState({
    width: 0,
    height: 0,
  });

  const nameInputRef = useRef<HTMLInputElement>(null);
  const hrefInputRef = useRef<HTMLInputElement>(null);
  const colorSelectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const storedData = JSON.parse(
      LocalStorageHelper.getItem<any>(LocalStorageKeys.CUSTOM_HOMEPAGE),
    );
    if (storedData) {
      const widgetInstances = storedData.pages.default;
      const widgetInstance =
        widgetInstances.find(
          (widget: any) =>
            JSON.stringify(widget.settings?.links) === JSON.stringify(value),
        ) ||
        widgetInstances.find(
          (widget: any) =>
            widget.id.includes('links') && !widget.settings.links,
        );

      if (widgetInstance) {
        setWidgetDimensions({
          width: widgetInstance.layout.w,
          height: widgetInstance.layout.h,
        });
      }
    }
  }, [value]);

  const themeColors = useMemo(
    () =>
      Object.entries(theme.palette)
        .filter(([_, color]) => (color as any)?.main)
        .map(([key, color]) => ({
          key,
          colorCode: (color as any).main,
        })),
    [theme.palette],
  );

  const isEditMode = editIndex !== null;
  const linkAtEditIndex = isEditMode ? value[editIndex] : null;

  function getRemainingLinks() {
    const maxCards = Math.floor(
      Math.min(widgetDimensions.width - 1, 4) * (widgetDimensions.height - 1),
    );
    return maxCards - value.length;
  }
  const remainingLinks = getRemainingLinks();

  function resetFormFields() {
    if (nameInputRef.current) nameInputRef.current.value = '';
    if (hrefInputRef.current) hrefInputRef.current.value = '';
    if (colorSelectRef.current) colorSelectRef.current.value = 'primary';
  }

  function populateFormFields(link: ILinkItem) {
    if (nameInputRef.current) nameInputRef.current.value = link.name;
    if (hrefInputRef.current) hrefInputRef.current.value = link.href;
    if (colorSelectRef.current) colorSelectRef.current.value = link.color;
  }

  function validate(link: ILinkItem | null): boolean {
    if (!link) return false;

    const { name, href, color } = link;
    const trimmedName = name.trim();
    const trimmedHref = href.trim();

    const newErrors = {
      name: (() => {
        if (!trimmedName) {
          return 'Name is required.';
        }
        if (value.some((l, i) => l.name === trimmedName && i !== editIndex)) {
          return 'Name already exists.';
        }
        return '';
      })(),
      href: !trimmedHref
        ? 'URL is required.'
        : (() => {
            if (!isValidUrl(trimmedHref)) {
              return 'Invalid URL.';
            }
            if (
              value.some((l, i) => l.href === trimmedHref && i !== editIndex)
            ) {
              return 'URL already exists.';
            }
            return '';
          })(),
      color: !color ? 'Color is required.' : '',
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  }

  function handleDialogClose() {
    setOpen(false);
    setEditIndex(null);
    setErrors({ name: '', href: '', color: '' });
  }

  function handleSave() {
    const name = nameInputRef.current?.value || '';
    const href = hrefInputRef.current?.value || '';
    const color = colorSelectRef.current?.value || 'primary';

    const link: ILinkItem = {
      name,
      href,
      color: color as keyof Theme['palette'],
    };

    if (!validate(link)) return;

    const updatedLinks = [...value];
    if (editIndex !== null) updatedLinks[editIndex] = link;
    else updatedLinks.push(link);

    onChange(updatedLinks);
    handleDialogClose();
  }

  function handleMove(index: number, direction: MoveDirection) {
    const updatedLinks = [...value];
    if (direction === MoveDirection.Up && index > 0) {
      [updatedLinks[index], updatedLinks[index - 1]] = [
        updatedLinks[index - 1],
        updatedLinks[index],
      ];
    } else if (
      direction === MoveDirection.Down &&
      index < updatedLinks.length - 1
    ) {
      [updatedLinks[index], updatedLinks[index + 1]] = [
        updatedLinks[index + 1],
        updatedLinks[index],
      ];
    }
    onChange(updatedLinks);
  }

  const columns: TableColumn<ILinkItem & { index: number }>[] = [
    { title: 'Name', field: 'name', sorting: false },
    { title: 'URL', field: 'href', sorting: false },
    {
      title: 'Color',
      field: 'color',
      sorting: false,
      render: ({ color }) => (
        <div
          className={cx(classes.colorCircle)}
          style={{
            backgroundColor:
              (theme.palette[color] as PaletteColor)?.main || color,
          }}
        />
      ),
    },
    {
      field: 'actions',
      sorting: false,
      render: ({ index }) => (
        <div className={cx(classes.actionButtons)}>
          <IconButton
            onClick={() => handleMove(index, MoveDirection.Up)}
            disabled={index === 0}
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            aria-label="down"
            onClick={() => handleMove(index, MoveDirection.Down)}
            disabled={index === value.length - 1}
          >
            <ArrowDownwardIcon />
          </IconButton>
          <IconButton
            aria-label="edit"
            onClick={() => {
              populateFormFields(value[index]);
              setEditIndex(index);
              setOpen(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => onChange(value.filter((_, i) => i !== index))}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const rows = value.map((item, index) => ({ ...item, index }));

  useEffect(() => {
    if (open && nameInputRef.current) nameInputRef.current.focus();
  }, [open]);

  return (
    <div>
      <Typography variant="body1">
        The number of links visible within your widget depends on its size. You
        can still add{' '}
        <span className={cx(classes.remainingLinks)}>{remainingLinks}</span>{' '}
        {remainingLinks === 1 ? 'link' : 'links'}.
      </Typography>

      <Button
        variant="contained"
        className={cx(classes.addLinkButton)}
        startIcon={<AddIcon />}
        onClick={() => {
          resetFormFields();
          setEditIndex(null);
          setOpen(true);
        }}
        disabled={remainingLinks <= 0}
      >
        New link
      </Button>

      <Table
        options={{ search: true, paging: false }}
        columns={columns}
        data={rows}
      />

      {open && (
        <div className={cx(classes.dialog)}>
          <DialogTitle className={cx(classes.title)}>
            {isEditMode ? 'Edit link' : 'Add new link'}
            <IconButton
              onClick={handleDialogClose}
              className={cx(classes.closeButton)}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <TextField
              id="test-link-name"
              label="Name*"
              fullWidth
              margin="dense"
              variant="standard"
              error={!!errors.name}
              helperText={
                errors.name ||
                'Name that will be shown for the link in the widget.'
              }
              defaultValue={linkAtEditIndex?.name || ''}
              inputRef={nameInputRef}
            />

            <TextField
              id="test-link-url"
              label="URL*"
              fullWidth
              margin="dense"
              variant="standard"
              error={!!errors.href}
              helperText={errors.href || 'Web address the link will point to.'}
              defaultValue={linkAtEditIndex?.href || ''}
              inputRef={hrefInputRef}
            />

            <TextField
              label="Color*"
              fullWidth
              margin="dense"
              variant="standard"
              select
              defaultValue={linkAtEditIndex?.color || 'primary'}
              error={!!errors.color}
              helperText={
                errors.color || 'The color that the link card will have.'
              }
              inputRef={colorSelectRef}
            >
              {themeColors.map(({ key, colorCode }) => (
                <MenuItem key={key} value={key}>
                  <div
                    className={cx(classes.colorPreviewSelect)}
                    style={{ backgroundColor: colorCode }}
                  />
                </MenuItem>
              ))}
            </TextField>

            <DialogActions className={cx(classes.dialogActionButtons)}>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button onClick={handleSave}>Save</Button>
            </DialogActions>
          </DialogContent>
        </div>
      )}
    </div>
  );
}
